import { Check, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { usePlans, useStartCheckout } from '@/hooks/use-subscription';
import { cn } from '@/lib/utils';

export function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const { data: plans, isLoading } = usePlans();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const startCheckout = useStartCheckout();

  const handleCta = (plan: { slug: string; price_monthly_cents: number }) => {
    if (plan.price_monthly_cents === 0) {
      navigate('/join');
      return;
    }
    if (!isAuthenticated) {
      navigate(`/join?plan=${plan.slug}&billing=${billing}`);
      return;
    }
    startCheckout.mutate({ planSlug: plan.slug, billing });
  };

  return (
    <div className="py-20 md:py-28">
      <div className="container-narrow text-center">
        <div className="eyebrow">Pricing</div>
        <h1 className="mt-5 font-display text-5xl leading-[1.02] tracking-tight md:text-7xl">
          Rebuilding costs less
          <span className="block italic text-terracotta-600">than you'd think.</span>
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-lg text-foreground/70">
          One membership, everything included. Cancel anytime — no guilt, no fine print.
        </p>

        <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-ink/15 bg-card p-1">
          {(['monthly', 'yearly'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBilling(b)}
              className={cn(
                'rounded-full px-5 py-2 text-sm font-medium transition-all',
                billing === b
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {b === 'monthly' ? 'Monthly' : 'Yearly'}
              {b === 'yearly' && (
                <span className="ml-2 rounded-full bg-sage-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sage-800">
                  Save 20%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="mt-16 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {!isLoading && plans && (
        <div className="container-wide mt-16 grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => {
            const price =
              billing === 'monthly'
                ? p.price_monthly_cents
                : Math.round(p.price_yearly_cents / 12);
            const isMid = i === 1;

            return (
              <div
                key={p.id}
                className={cn(
                  'relative flex flex-col rounded-2xl border p-8 transition-all',
                  isMid
                    ? 'border-terracotta-300 bg-card shadow-xl md:-translate-y-4'
                    : 'border-ink/10 bg-card',
                )}
              >
                {isMid && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold uppercase tracking-widest text-primary-foreground">
                    Most chosen
                  </div>
                )}

                <div>
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  {p.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                  )}
                </div>

                <div className="mt-7">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-5xl tracking-tight">
                      {price === 0 ? 'Free' : `£${(price / 100).toFixed(0)}`}
                    </span>
                    {price > 0 && (
                      <span className="text-sm text-muted-foreground">/ month</span>
                    )}
                  </div>
                  {billing === 'yearly' && p.price_yearly_cents > 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      £{(p.price_yearly_cents / 100).toFixed(0)} billed annually
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  size="lg"
                  variant={isMid ? 'default' : 'outline'}
                  className="mt-7"
                  onClick={() => handleCta(p)}
                  disabled={startCheckout.isPending}
                >
                  {startCheckout.isPending && startCheckout.variables?.planSlug === p.slug ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  {price === 0 ? 'Start free' : `Join with ${p.name}`}
                </Button>

                <ul className="mt-8 space-y-3 border-t border-ink/10 pt-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div className="container-narrow mt-24 text-center">
        <p className="font-display text-2xl italic leading-snug text-foreground/80">
          Financial hardship? We keep scholarship spots every month.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Email{' '}
          <a href="mailto:hello@gracefullyrebuilt.com" className="underline">
            hello@gracefullyrebuilt.com
          </a>{' '}
          and we'll take care of you.
        </p>
      </div>
    </div>
  );
}
